// src/pages/BlogDetailPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { fetchBlogs } from "../api/blogApi";
import { fetchBlogCategories } from "../api/blogCategoryApi";
import { toastError } from "../utils/toast";
import "./css/blogdetail.css";
import { withBase } from "../config/env";

function DetailShimmer() {
  return (
    <main className="blogs-wrap">
      <div className="blog-detail-grid">
        {/* MAIN shimmer */}
        <article className="blog-detail">
          <header className="blog-detail-header">
            <div className="blog-meta">
              <div className="shimmer-line short" />
              <div className="shimmer-line title-lg" />
              <div className="shimmer-line meta" />
            </div>
            <div className="blog-detail-cover">
              <div className="shimmer-block hero" />
            </div>
          </header>

          <section className="blog-detail-content">
            <div className="shimmer-line" />
            <div className="shimmer-line" />
            <div className="shimmer-line" />
            <div className="shimmer-line" />
            <div className="shimmer-line short" />
          </section>

          <footer className="blog-detail-footer">
            <div className="shimmer-chip-row">
              <span className="shimmer-chip" />
              <span className="shimmer-chip" />
              <span className="shimmer-chip" />
            </div>
          </footer>
        </article>

        {/* SIDEBAR shimmer */}
        <aside className="blog-detail-aside" aria-label="Sidebar">
          <div className="sidebar-card">
            <div className="sidebar-title shimmer-title" />
            <div className="shimmer-pill" />
            <div className="shimmer-pill" />
            <div className="shimmer-pill" />
            <div className="shimmer-pill" />
          </div>

          <div className="sidebar-card">
            <div className="sidebar-title shimmer-title" />
            <div className="list-tile-link shimmer-tile">
              <div className="list-tile-media shimmer-block" />
              <div className="list-tile-body">
                <div className="shimmer-line small" />
                <div className="shimmer-line tiny" />
                <div className="shimmer-line tiny short" />
              </div>
            </div>
            <div className="list-tile-link shimmer-tile">
              <div className="list-tile-media shimmer-block" />
              <div className="list-tile-body">
                <div className="shimmer-line small" />
                <div className="shimmer-line tiny" />
                <div className="shimmer-line tiny short" />
              </div>
            </div>
            <div className="list-tile-link shimmer-tile">
              <div className="list-tile-media shimmer-block" />
              <div className="list-tile-body">
                <div className="shimmer-line small" />
                <div className="shimmer-line tiny" />
                <div className="shimmer-line tiny short" />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default function BlogDetailPage() {
  const { id } = useParams();
  const { state } = useLocation();
  const [blog, setBlog] = useState(state?.blog || null);
  const [loading, setLoading] = useState(!state?.blog);

  const [categories, setCategories] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);

  // Load blog if not passed in state
  useEffect(() => {
    if (state?.blog) return; // already have it
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const all = await fetchBlogs();
        const found =
          Array.isArray(all) &&
          all.find(
            (b) =>
              String(b?.id) === id ||
              String(b?._id) === id ||
              String(b?.slug) === id
          );
        if (mounted) setBlog(found || null);
        if (!found) toastError("Blog not found.");
      } catch {
        toastError("Failed to load blog.");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, state?.blog]);

  // Load categories (Blogs only) + all blogs (for recent list)
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const catsRes = await fetchBlogCategories();
        const list = Array.isArray(catsRes) ? catsRes : catsRes?.data || [];
        const blogsOnly = list.filter(
          (c) => (c?.type || "").toLowerCase() === "blogs"
        );
        if (on) setCategories(blogsOnly);
      } catch {
        setCategories([]);
      }
      try {
        const b = await fetchBlogs();
        if (on) setAllBlogs(Array.isArray(b) ? b : []);
      } catch {
        // ignore
      }
    })();
    return () => {
      on = false;
    };
  }, []);

  // Build an array of categories for this blog (safe when blog is null)
  const inlineCategories = useMemo(() => {
    const b = blog || {};
    if (Array.isArray(b.categories) && b.categories.length) return b.categories;
    if (Array.isArray(b.tags) && b.tags.length) return b.tags;
    if (b.category_name) return [b.category_name];
    return [];
  }, [blog]);

  // Recent 3 posts (exclude current)
  const recentBlogs = useMemo(() => {
    const getDate = (x) =>
      new Date(x?.created_at || x?.createdAt || x?.date || 0).getTime();
    return [...allBlogs]
      .filter(
        (b) => String(b?.id ?? b?._id ?? b?.slug) !== String(id)
      )
      .sort((a, b) => getDate(b) - getDate(a))
      .slice(0, 3);
  }, [allBlogs, id]);

  if (loading) {
    return <DetailShimmer />;
  }

  if (!blog) {
    return (
      <main className="blogs-wrap">
        <section className="blogs-section">
          <div className="blogs-empty">
            <h3>Blog Not Found</h3>
            <p>The requested blog post doesn't exist or may have been removed.</p>
            <Link to="/blogs" className="blog-readmore">
              ← Return to Blog List
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const title = blog?.title || "Untitled";
  const thumbnail = withBase(blog?.thumbnail);
  const createdAt = blog?.created_at || blog?.createdAt || blog?.date;
  const content = blog?.content || blog?.body || "";

  const getExcerpt = (html, len = 80) => {
    if (!html) return "";
    const txt = String(html).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    return txt.length > len ? txt.slice(0, len) + "…" : txt;
  };

  return (
    <main className="blogs-wrap">
      <div className="blog-detail-grid">
        {/* MAIN */}
        <article className="blog-detail">
          <header className="blog-detail-header">
            <div className="blog-meta">
              {inlineCategories.length > 0 && (
                <div className="blog-categories">
                  {inlineCategories.slice(0, 3).map((category, index) => {
                    const catObj = categories.find((c) => c.title === category);
                    const catId = catObj?.id;
                    return (
                      <Link
                        key={index}
                        to={
                          catId
                            ? `/blogs?category=${catId}&categoryName=${encodeURIComponent(
                                category
                              )}`
                            : `/blogs?categoryName=${encodeURIComponent(
                                category
                              )}`
                        }
                        className="blog-category"
                      >
                        {category}
                      </Link>
                    );
                  })}
                </div>
              )}
              <h1 className="blog-detail-title">{title}</h1>
              <div className="blog-author-date">
                {createdAt && (
                  <time className="blog-detail-date">
                    {new Date(createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                )}
              </div>
            </div>

            {thumbnail && (
              <div className="blog-detail-cover">
                <img
                  src={thumbnail}
                  alt={title}
                  className="blog-thumbnail"
                  loading="lazy"
                />
              </div>
            )}
          </header>

          <section className="blog-detail-content">
            {/<[a-z][\s\S]*>/i.test(String(content)) ? (
              <div
                className="blog-html"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <div className="blog-text">
                {String(content)
                  .split("\n")
                  .map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
              </div>
            )}
          </section>

          <footer className="blog-detail-footer">
            <div className="blog-tags">
              {inlineCategories.length > 0 && (
                <>
                  <span>Tags:</span>
                  {inlineCategories.map((category, index) => (
                    <span key={index} className="blog-tag">
                      {category}
                    </span>
                  ))}
                </>
              )}
            </div>

            <div
              className="blog-actions"
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginTop: 12,
              }}
            >
              <Link to="/blogs" className="blog-readmore">
                ← Back to All Articles
              </Link>
              <Link to="/contact" className="blog-readmore">
                Inquiry Now →
              </Link>
            </div>
          </footer>
        </article>

        {/* SIDEBAR */}
        <aside className="blog-detail-aside" aria-label="Sidebar">
          <div className="sidebar-card">
            <h3 className="sidebar-title">Categories</h3>
            <div className="cat-list">
              <Link to="/blogs" className="cat-item">
                <span>All</span>
              </Link>
              {categories.map((c) => (
                <Link
                  key={c.id ?? c.title}
                  to={`/blogs?category=${c.id}&categoryName=${encodeURIComponent(
                    c.title
                  )}`}
                  className="cat-item"
                >
                  <span>{c.title}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="sidebar-card">
            <h3 className="sidebar-title small">Recent Posts</h3>
            <ul className="recent-posts-list">
              {recentBlogs.map((b) => {
                const bid = b?.id ?? b?._id ?? b?.slug;
                const btitle = b?.title || "Untitled";
                const cover = withBase(b?.thumbnail);
                const bdate = b?.created_at || b?.createdAt || b?.date;
                const subtitle = getExcerpt(b?.excerpt || b?.content, 80);

                return (
                  <li key={`recent-${bid}`} className="list-tile">
                    {/* IMPORTANT: don't pass state to force fetch + shimmer */}
                    <Link to={`/blogs/${bid}`} className="list-tile-link">
                      <div className="list-tile-media">
                        {cover ? (
                          <img src={cover} alt={btitle} />
                        ) : (
                          <div className="list-tile-placeholder" />
                        )}
                      </div>
                      <div className="list-tile-body">
                        <h4 className="list-tile-title">{btitle}</h4>
                        {subtitle && (
                          <p className="list-tile-subtitle">{subtitle}</p>
                        )}
                        {bdate && (
                          <time className="list-tile-date">
                            {new Date(bdate).toLocaleDateString()}
                          </time>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
              {recentBlogs.length === 0 && (
                <li className="recent-empty">No recent posts</li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </main>
  );
}
